import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CForm,
  CFormGroup,
  CLabel,
  CInput,
  CButton,
  CRow,
  CCol,
  CInputGroup,
  CInputGroupPrepend,
  CInputGroupText,
} from "@coreui/react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GridReadyEvent } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { createItem, updateItem, getList } from "../../api/generics";
import DocumentVerification, {
  DocumentVerificationItem,
  newDocumentVerification,
} from "../../models/document-verification";
import { SUCCESS } from "../../utils/constants/tags";
import { errorAlert, successAlert, warningAlert } from "../utils/messages";
import { BsUpcScan } from "react-icons/bs";
import Asset from "../../models/asset";
import AssetsSelect from "../asset/AssetsSelect";
import Document from "../../models/document";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { FieldErrors } from "../form/FieldErrors";
import Errors, { getFieldErrors } from "../../models/errors";

const spanishLocaleText = {
  // Filter translations
  filterOoo: "Filtrar...",
  equals: "Igual",
  notEqual: "No igual",
  lessThan: "Menor que",
  greaterThan: "Mayor que",
  inRange: "En rango",
  contains: "Contiene",
  notContains: "No contiene",
  startsWith: "Comienza con",
  endsWith: "Termina con",
  andCondition: "Y",
  orCondition: "O",
  applyFilter: "Aplicar",
  resetFilter: "Reiniciar",
  clearFilter: "Limpiar",

  // Other common translations
  page: "Página",
  to: "a",
  of: "de",
  nextPage: "Siguiente página",
  lastPage: "Última página",
  firstPage: "Primera página",
  previousPage: "Página anterior",
  loadingOoo: "Cargando...",
  noRowsToShow: "Sin datos para mostrar",

  // translations for ag-Grid:
  // https://www.ag-grid.com/javascript-data-grid/localisation/
};

interface DocumentVerificationCopyFormProps {
  initialVerification?: DocumentVerification;
  onCancel: () => void;
  onSuccess: () => void;
}

const DocumentVerificationCopyForm: React.FC<
  DocumentVerificationCopyFormProps
> = ({ initialVerification, onCancel, onSuccess }) => {
  const [verification, setVerification] = useState<DocumentVerification>(
    initialVerification || {
      ...newDocumentVerification(),
    }
  );
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const didMountRef = useRef(false);
  const [barcodeInput, setBarcodeInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id
  );

  const barcodeInputRef = useRef<HTMLInputElement>(null);

  const isEditing = false;

  const isExpired = (expirationDate?: string): boolean => {
    if (!expirationDate) return false;
    const now = new Date();
    const expDate = new Date(expirationDate);
    return now > expDate;
  };

  const handleInputChange = (field: keyof DocumentVerification, value: any) => {
    setVerification((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleItemVerifiedChange = (index: number, verified: boolean) => {
    const updatedItems = [...verification.items];
    updatedItems[index] = {
      ...updatedItems[index],
      verified,
      verificationDate: verified ? new Date().toISOString() : undefined,
    };

    setVerification((prev) => ({
      ...prev,
      items: updatedItems,
    }));
  };

  const handleBarcodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!barcodeInput.trim()) return;

    const documentId = parseInt(barcodeInput.trim(), 10);

    if (isNaN(documentId)) {
      warningAlert("Por favor, ingrese un ID de documento válido (número)");
      setBarcodeInput("");
      return;
    }

    const itemIndex = verification.items.findIndex(
      (item) => parseInt(item.barcode!) === documentId
    );

    if (itemIndex !== -1) {
      const updatedItems = [...verification.items];
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        verified: true,
        verificationDate: new Date().toISOString(),
      };

      setVerification((prev) => ({
        ...prev,
        items: updatedItems,
      }));

      successAlert(
        `Documento "${updatedItems[itemIndex].documentName}" marcado como verificado.`
      );
    } else {
      warningAlert("No se encontró un documento con el ID ingresado.");
    }

    setBarcodeInput("");
    setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 100);
  };

  const handleItemObsChange = (index: number, value: string) => {
    const updatedItems = [...verification.items];
    updatedItems[index] = {
      ...updatedItems[index],
      obs: value,
    };

    setVerification((prev) => ({
      ...prev,
      items: updatedItems,
    }));
  };

  const handleFetchAssetsByTrip = useCallback(async () => {
    if (!companyId || !verification.tripNumber || selectedAssets.length === 0) {
      warningAlert(
        "Debe ingresar la compañía, el número de viaje y seleccionar el productor padre."
      );
      return;
    }
    try {
      const response = await getList<Asset>(
        "/assets/by_trip_id/",
        1000,
        0,
        new Map([
          ["company_id", companyId.toString()],
          ["trip_id", parseInt(verification.tripNumber).toString()],
          ["parent_asset_id", selectedAssets[0].id!.toString()],
        ])
      );
      if (response.status === SUCCESS && response.data?.items?.length) {
        setSelectedAssets((prev) => {
          const newAssets = [...prev, ...response.data!.items];
          fetchDocumentsForAssets(response.data!.items);
          return newAssets;
        });

        successAlert("Productores encontrados y documentos cargados.");
      } else if (response.status === "server_error") {
        errorAlert(response.detail || "Error del servidor al buscar productores.");
      } else {
        warningAlert("No se encontraron productores para este viaje.");
      }
    } catch (error) {
      errorAlert("Error al buscar productores para el viaje.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, verification.tripNumber, selectedAssets]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitting(true);

      try {
        const cleanedVerification = {
          ...verification,
          id: undefined, // Ensure no ID to create new verification
          userId: userId,
          companyId: companyId,
          username: email,
          items: verification.items.map((item) => ({
            ...item,
            id: undefined, // Remove item IDs
            verification_date: item.verified
              ? item.verificationDate
              : undefined,
          })),
          assets: selectedAssets,
        };

        const response = isEditing
          ? await updateItem(
              `/document_verifications/${verification.id}/`,
              cleanedVerification
            )
          : await createItem("/document_verifications/", cleanedVerification);

        if (response.status === SUCCESS) {
          successAlert(
            isEditing
              ? "Verificación actualizada con éxito."
              : "Verificación creada con éxito."
          );
          onSuccess();
        } else {
          errorAlert("Error al guardar la verificación.");
          setErrors(response.errors as Errors);
        }
      } catch (error) {
        errorAlert("Error al guardar la verificación.");
      } finally {
        setSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [verification, selectedAssets, companyId]
  );

  const fetchDocumentsForAssets = async (assets: Asset[]) => {
    if (assets.length === 0) {
      setVerification((prev) => ({ ...prev, items: [] }));
      return;
    }

    try {
      let allDocuments: Document[] = [];

      for (const asset of assets) {
        const response = await getList<Document>(
          "/documents/",
          1000,
          0,
          new Map([["asset_id", asset.id!.toString()]])
        );

        if (response.status === SUCCESS) {
          for (const doc of response.data?.items || []) {
            if (!doc.asset) {
              doc.asset = asset;
            }
            allDocuments.push(doc);
          }
        }
      }

      const uniqueDocuments = allDocuments.filter(
        (doc, index, self) => index === self.findIndex((d) => d.id === doc.id)
      );

      const fetchedItems = uniqueDocuments
        .filter(
          (doc) =>
            doc.id !== undefined && doc.name !== undefined && doc.name !== null
        )
        .map((document) => {
          const existing = verification.items.find(
            (item) => item.documentId === document.id
          );
          let assetName = "";
          if (existing && existing.asset && existing.asset.name) {
            assetName = existing.asset.name;
          }

          if (assetName === "" && document.asset && document.asset.name) {
            assetName = document.asset.name;
          }

          return existing
            ? {
                ...existing,
                documentId: document.id!,
                documentName: document.name!,
                categoryId: document.documentCategoryId,
                barcode: document.barcode,
                expirationDate: document.expirationDate,
                assetName: assetName,
                verified: false, // Reset verification status
                verificationDate: undefined, // Clear verification date
                obs: "", // Clear observations
              }
            : {
                documentId: document.id!,
                documentName: document.name!,
                categoryId: document.documentCategoryId,
                barcode: document.barcode,
                verified: false,
                verification_date: undefined,
                obs: "",
                expirationDate: document.expirationDate,
                assetName: assetName,
              };
        }) as DocumentVerificationItem[];

      setVerification((prev) => ({
        ...prev,
        items: fetchedItems,
      }));
    } catch (error) {
      warningAlert("Error al cargar los documentos de los productores");
    }
  };

  const handleAssetsChange = (assets: readonly Asset[]) => {
    setSelectedAssets([...assets]);
    fetchDocumentsForAssets([...assets]);
  };

  // AG Grid column definitions
  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Productor",
        field: "assetName",
        sortable: true,
        filter: true,
        flex: 1,
        autoHeight: true,
        width: 200,
        cellStyle: {
          backgroundColor: "#f8f9fa",
        },
        valueGetter: (params) =>
          params.data.asset?.name || params.data.assetName || "",
      },
      {
        headerName: "Documento",
        field: "documentName",
        sortable: true,
        filter: true,
        flex: 2,
        autoHeight: true,
        width: 200,
        cellStyle: { backgroundColor: "#f8f9fa" },
      },
      {
        headerName: "Fecha de Vencimiento",
        field: "expirationDate",
        sortable: true,
        filter: true,
        width: 180,
        cellStyle: (params) => {
          const expired = isExpired(params.value);
          return {
            backgroundColor: expired ? "#ffebee" : "transparent",
            color: expired ? "#c62828" : "inherit",
            fontWeight: expired ? "bold" : "normal",
          };
        },
        valueFormatter: (params) => {
          return params.value
            ? new Date(params.value).toLocaleDateString()
            : "Sin fecha";
        },
      },
      {
        headerName: "Verificado",
        field: "verified",
        sortable: true,
        filter: true,
        width: 120,
        cellRenderer: (params: any) => {
          const index = verification.items.findIndex(
            (item) => item.documentId === params.data.documentId
          );
          return (
            <input
              type="checkbox"
              checked={params.value}
              onChange={(e) =>
                handleItemVerifiedChange(index, e.target.checked)
              }
              style={{ cursor: "pointer", marginTop: "8px" }}
            />
          );
        },
      },
      {
        headerName: "Fecha de Verificación",
        field: "verificationDate",
        sortable: true,
        filter: true,
        width: 200,
        cellStyle: { backgroundColor: "#f8f9fa" },
        valueFormatter: (params) => {
          return params.value ? new Date(params.value).toLocaleString() : "";
        },
      },
      {
        headerName: "Observaciones",
        field: "obs",
        sortable: false,
        filter: false,
        flex: 2,
        editable: true,
        cellEditor: "agTextCellEditor",

        onCellValueChanged: (params) => {
          const index = verification.items.findIndex(
            (item) => item.documentId === params.data.documentId
          );
          handleItemObsChange(index, params.newValue || "");
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [verification.items]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
    }),
    []
  );

  const onGridReady = useCallback((params: GridReadyEvent) => {
    params.api.sizeColumnsToFit();
  }, []);

  useEffect(() => {
    barcodeInputRef.current?.focus();

    if (
      initialVerification !== undefined &&
      initialVerification.assets !== undefined &&
      !didMountRef.current
    ) {
      const fetchInitialData = async (assets: Asset[]) => {
        // Clean IDs and reset verification data for a new verification
        const cleanedVerification = {
          ...initialVerification,
          id: undefined,
          tripNumber: "", // Clear trip number
          date: new Date().toISOString(), // Set current date
          items: [], // Items will be cleaned in fetchDocumentsForAssets
        };

        setVerification(cleanedVerification);
        setSelectedAssets(assets);
        await fetchDocumentsForAssets(assets);
      };

      fetchInitialData(initialVerification.assets);
      didMountRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialVerification]);

  const { id: userId, email } = useSelector((state: RootState) => state.user);

  return (
    <CCard>
      <CCardHeader>
        <h4>Nueva Verificación desde existente</h4>
      </CCardHeader>
      <CCardBody>
        <CForm onSubmit={handleSubmit}>
          <fieldset disabled={submitting}>
            {/* Basic Verification Information */}
            <CRow>
              <CCol md={6}>
                <CFormGroup>
                  <CLabel>Número de Viaje</CLabel>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <CInput
                      type="text"
                      value={verification.tripNumber || ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleInputChange("tripNumber", e.target.value)
                      }
                      placeholder="Ingrese el número de viaje"
                      style={{ marginRight: 8 }}
                    />
                    <CButton
                      color="info"
                      type="button"
                      onClick={handleFetchAssetsByTrip}
                      disabled={submitting}
                      style={{ whiteSpace: "nowrap" }}
                    >
                      <i className="fa fa-search" aria-hidden="true"></i>
                    </CButton>
                  </div>
                  <FieldErrors
                    errors={getFieldErrors("tripNumber", errors) as string[]}
                  ></FieldErrors>
                </CFormGroup>
              </CCol>
              <CCol md={6}>
                <CFormGroup>
                  <CLabel>Área</CLabel>
                  <CInput
                    type="text"
                    value={verification.area || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange("area", e.target.value)
                    }
                    placeholder="Ingrese el área"
                  />
                  <FieldErrors
                    errors={getFieldErrors("area", errors) as string[]}
                  ></FieldErrors>
                </CFormGroup>
              </CCol>
            </CRow>

            <CRow>
              <CCol md={6}>
                <CFormGroup>
                  <CLabel>Usuario</CLabel>
                  <CInput
                    type="text"
                    value={email || "Cargando..."}
                    readOnly
                    placeholder="Nombre del usuario"
                    required
                  />
                </CFormGroup>
              </CCol>
              <CCol md={6}>
                <CFormGroup>
                  <CLabel>Fecha</CLabel>
                  <CInput
                    type="datetime-local"
                    value={verification.date.slice(0, 16)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange("date", e.target.value + ":00Z")
                    }
                    required
                  />
                </CFormGroup>
              </CCol>
            </CRow>

            <CRow>
              <CCol md={12}>
                <CFormGroup>
                  <CLabel>Productores</CLabel>
                  <AssetsSelect
                    value={selectedAssets}
                    onChange={handleAssetsChange}
                    isDisabled={submitting}
                  />
                </CFormGroup>
              </CCol>
            </CRow>

            {/* Barcode Scanner */}
            <CRow className="mb-4">
              <CCol md={12}>
                <CFormGroup>
                  <CLabel>Escáner de Código de Barras</CLabel>
                  <CInputGroup>
                    <CInputGroupPrepend>
                      <CInputGroupText>
                        <BsUpcScan />
                      </CInputGroupText>
                    </CInputGroupPrepend>
                    <CInput
                      innerRef={barcodeInputRef}
                      type="text"
                      value={barcodeInput}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value.replace(/\D/g, "");
                        setBarcodeInput(value);
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleBarcodeSubmit(e);
                        }
                      }}
                      placeholder="Escanee o ingrese el ID del documento"
                      autoComplete="off"
                    />
                    <CInputGroupPrepend>
                      <CButton
                        color="info"
                        type="button"
                        onClick={() => barcodeInputRef.current?.focus()}
                      >
                        Activar
                      </CButton>
                    </CInputGroupPrepend>
                  </CInputGroup>
                </CFormGroup>
              </CCol>
            </CRow>

            {/* Document Verification Items Table with AG Grid */}
            <CFormGroup>
              <CLabel>Documentos para Verificar</CLabel>
              <div
                className="ag-theme-alpine"
                style={{ height: 520, width: "100%" }}
              >
                <AgGridReact
                  rowData={verification.items}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  pagination={true}
                  paginationPageSize={10}
                  onGridReady={onGridReady}
                  animateRows={true}
                  rowHeight={40}
                  headerHeight={40}
                  domLayout="normal"
                  suppressCellFocus={false}
                  localeText={spanishLocaleText}
                  theme="legacy"
                  initialState={{
                    sort: {
                      sortModel: [
                        { colId: "assetName", sort: "asc" },
                        { colId: "documentName", sort: "asc" }, // Default sort by asset name ascending
                      ],
                    },
                  }}
                />
              </div>
            </CFormGroup>

            {/* Action Buttons */}
            <CRow>
              <CCol>
                <CButton type="submit" color="primary" disabled={submitting}>
                  {submitting
                    ? "Guardando..."
                    : isEditing
                    ? "Actualizar Verificación"
                    : "Crear Verificación"}
                </CButton>
                <CButton
                  type="button"
                  color="secondary"
                  className="ml-2"
                  onClick={onCancel}
                  disabled={submitting}
                >
                  Cancelar
                </CButton>
              </CCol>
            </CRow>
          </fieldset>
        </CForm>
      </CCardBody>
    </CCard>
  );
};

export default DocumentVerificationCopyForm;
